import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Image, message, Select, Space, Tabs, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import btnStyles from "../../../styles/Custom/Button.module.css";
import InPatientModal from "./InPatientModal";
import {
  getIpAdmissionApi,
  syncBillsApi,
} from "../../../services/receptionist.services";
import { getCookie } from "../../../helpers/localStorage";
import { FacilityID } from "../../../constants/defaultKeys";
import { UserContext } from "../../../context/UserContext";
import IpPatientTable, { IP_ADMISSION_PAGE_SIZE } from "./IpPatientTable";
import CustomLayout from "../../Layout/CustomLayout";
import { useNavigate } from "react-router-dom";
import useQuery from "../../../hooks/useQuery";
import {
  updateUrlSearchParams,
  parsePageParam,
  parseSearchParam,
} from "../../../helpers/urlFilters";
import ReceptionistSideMenu from "../../Layout/CustomLayout/ReceptionistSideMenu";
import textStyles from "../../../styles/Custom/Typography.module.css";
import inputStyles from "../../../styles/Custom/Input.module.css";
import { SyncOutlined } from "@ant-design/icons";
import { UiDataTable } from "../../ui";
import ReceptionistCountBadge from "../ReceptionistCountBadge";
import commonStyles from "../ReceptionistCommon.module.css";
import "./Inpatients.css";

const TAB_LABELS = {
  active: "Active",
  discharged: "Discharged",
  all: "All",
};

const TAB_KEYS = ["active", "discharged", "all"];

const renderTabLabel = (key, count) => (
  <span className={commonStyles.tabLabelWithBadge}>
    {TAB_LABELS[key]}
    <ReceptionistCountBadge count={count} />
  </span>
);

const Inpatients = ({ refresh }) => {
  // const query = useParams();
  const navigate = useNavigate();
  const params = useQuery();
  const currentTab = params?.status || "active";
  const page = parsePageParam(params);
  const searchQuery = parseSearchParam(params);
  const { userDetails } = useContext(UserContext);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [ipRefresh, setIpRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tabCounts, setTabCounts] = useState({
    active: null,
    discharged: null,
    all: null,
  });

  const facilityId =
    Number(getCookie(FacilityID)) || userDetails?.currFacility;

  const refreshIpPage = () => {
    setTabCounts({ active: null, discharged: null, all: null });
    setIpRefresh((prev) => !prev);
  };

  const tabItems = useMemo(
    () =>
      TAB_KEYS.map((key) => ({
        key,
        label: renderTabLabel(key, tabCounts[key]),
      })),
    [tabCounts]
  );

  const statusOptions = useMemo(
    () =>
      TAB_KEYS.map((key) => ({
        value: key,
        label: renderTabLabel(key, tabCounts[key]),
      })),
    [tabCounts]
  );

  const fetchIpAdmissions = () => {
    let params = {
      page: page,
      limit: IP_ADMISSION_PAGE_SIZE,
      facility: facilityId,
      list:
        currentTab === "all"
          ? "all"
          : currentTab === "active"
          ? "active"
          : "discharged",
    };

    if (searchQuery) {
      params.search = searchQuery;
      params.page = 1;
    }

    const queryString = new URLSearchParams(params);
    setLoading(true);

    getIpAdmissionApi(queryString)
      .then((res) => {
        setData(res?.data);
        if (!searchQuery) {
          setTabCounts((prev) => ({
            ...prev,
            [currentTab]: res?.data?.total_count ?? 0,
          }));
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchIpAdmissions();
  }, [facilityId, ipRefresh, refresh, searchQuery, page, currentTab]);

  const onContactSearch = (value) => {
    updateUrlSearchParams(navigate, {
      search: value?.target?.value || null,
      page: 1,
    });
  };

  const onChange = (key) => {
    updateUrlSearchParams(navigate, { status: key, page: 1, search: null });
  };

  const setPage = (page) => {
    updateUrlSearchParams(navigate, { page });
  };

  const onSync = () => {
    syncBillsApi()
      .then(() => {
        setTabCounts({ active: null, discharged: null, all: null });
        fetchIpAdmissions();
      })
      .catch((err) => message.error(err?.response?.data?.message));
  };

  const headerTabs = (
    <Tabs
      key={TAB_KEYS.map((key) => tabCounts[key]).join("-")}
      className="inpatientsHeaderTabs"
      onChange={onChange}
      activeKey={currentTab}
      items={tabItems}
      moreIcon={null}
    />
  );

  return (
    <CustomLayout
      layoutClassName="reception-layout"
      sideContent={
        params?.patient ? null : <ReceptionistSideMenu refresh={refresh} />
      }
      header={
        params?.patient ? (
          <>
            {userDetails?.activeOrg?.org_logo ? (
              <Image
                preview={false}
                src={userDetails?.activeOrg?.org_logo}
                alt={"Logo"}
                style={{
                  width: "40px",
                  marginTop: "10px",
                }}
              />
            ) : (
              userDetails?.activeOrg?.org_name
            )}
          </>
        ) : (
          <div className="inpatients-page-header">
            <Typography className={textStyles.h6}>In Patients</Typography>
            <div className="inpatients-header-tabs-desktop">
              {headerTabs}
            </div>
          </div>
        )
      }
    >
      {/* {data?.length === 0 ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "80%",
          }}
        >
          <Button
            type="primary"
            className={btnStyles.mdBtn}
            onClick={() => setOpen(true)}
          >
            <PlusOutlined /> Add In Patient
          </Button>
        </div>
      ) : ( */}
      <>
        {!params?.patient && (
          <div className="inpatients-status-select-mobile">
            <Select
              className={`inpatients-status-select ${inputStyles.mdSelect}`}
              value={currentTab}
              options={statusOptions}
              onChange={onChange}
            />
          </div>
        )}
        <UiDataTable
          showHeader={false}
          shellClassName="reception-patient-list-shell"
          onSearch={(e) => onContactSearch(e)}
          searchValue={searchQuery || ""}
          searchPlaceholder="Search for patient"
          toolbarTrailing={
            <Space className="inpatients-toolbar-actions" wrap>
              <Button
                onClick={onSync}
                ghost
                type="primary"
                className={btnStyles.mdBtn}
              >
                <SyncOutlined /> Sync Balance
              </Button>
              <Button
                type="primary"
                className={btnStyles.mdBtn}
                style={{
                  height: 44,
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "8px",
                  whiteSpace: "nowrap",
                }}
                onClick={() => setOpen(true)}
              >
                <PlusOutlined /> Add In Patient
              </Button>
            </Space>
          }
        >
          <IpPatientTable
            loading={loading}
            currentTab={currentTab}
            data={data}
            refreshIpPage={refreshIpPage}
            page={page}
            setPage={setPage}
          />
        </UiDataTable>
      </>
      {open && (
        <InPatientModal
          open={open}
          setOpen={setOpen}
          refreshIpPage={refreshIpPage}
          refresh={refresh}
        />
      )}
    </CustomLayout>
  );
};

export default Inpatients;
